import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { B } from '../constants';
import { collaborators as collaboratorsApi } from '../api';
import { useAuth } from '../AuthContext';
import AuthScreen from '../AuthScreen';
import { savePendingInvite, clearPendingInvite } from '../lib/pendingInvite';

const ERROR_MESSAGES = {
  403: 'This invite was sent to a different email address.',
  404: 'This invite link is invalid or the invite was removed.',
  409: 'This invite was already accepted by another account.',
};

// Screen mounted at /trips/invite/:token. Accepts a collaboration invite using
// the token in the URL. Signed-out users sign in here; the token is kept so a
// new account (sign up → verify email → sign in) still joins the trip.
export default function AcceptInviteScreen() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { user, loading, logout } = useAuth() || {};
  const [status, setStatus] = useState('idle'); // idle | accepting | success | error
  const [message, setMessage] = useState('');
  const [errorInfo, setErrorInfo] = useState(null);
  const [trip, setTrip] = useState(null);
  const [showAuth, setShowAuth] = useState(false);
  const acceptedFor = useRef(null);

  useEffect(() => {
    if (!token) { setStatus('error'); setMessage('Invalid invite link.'); return; }
    if (loading) return;
    if (!user) { savePendingInvite(token); return; }
    const key = `${token}:${user.id}`;
    if (acceptedFor.current === key) return;
    acceptedFor.current = key;
    setStatus('accepting');
    collaboratorsApi.accept(token)
      .then((res) => {
        clearPendingInvite();
        setTrip(res.trip || null);
        setStatus('success');
      })
      .catch((err) => {
        if (ERROR_MESSAGES[err.status]) clearPendingInvite();
        setErrorInfo({ status: err.status, ...(err.data || {}) });
        setStatus('error');
        setMessage(ERROR_MESSAGES[err.status] || err.message || 'Failed to accept invite.');
      });
  }, [token, user, loading]);

  const switchAccount = async () => {
    savePendingInvite(token);
    acceptedFor.current = null;
    setErrorInfo(null);
    setStatus('idle');
    setShowAuth(true);
    if (logout) await logout();
  };

  const btn = {
    background: B.forest, color: 'white', border: 'none', borderRadius: 8,
    padding: '12px 24px', fontWeight: 600, cursor: 'pointer', margin: '0 6px 8px',
  };
  const btnAlt = { ...btn, background: 'white', color: B.forest, border: `1.5px solid ${B.forest}` };

  const card = (children) => (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: B.sandPattern, padding: 24, boxSizing: 'border-box' }}>
      <div style={{ background: 'white', borderRadius: 16, padding: 32, maxWidth: 480, width: '100%', boxSizing: 'border-box', boxShadow: '0 12px 32px rgba(0,0,0,0.08)', textAlign: 'center' }}>
        {children}
      </div>
    </div>
  );

  if (!loading && !user && token) {
    return (
      <>
        {card(
          <>
            <h2 style={{ color: B.forest, marginTop: 0 }}>Sign in to accept invite</h2>
            <p style={{ color: B.muted }}>
              Sign in or create a GreatBreak account with the email this invite was sent to. You&apos;ll join the trip right after.
            </p>
            <button type="button" style={btn} onClick={() => setShowAuth(true)}>Sign in</button>
          </>,
        )}
        {showAuth && (
          <div role="dialog" aria-modal="true" aria-label="Sign in" style={{ position: 'fixed', inset: 0, zIndex: 10000, overflowY: 'auto', background: B.night }}>
            <AuthScreen onSuccess={() => setShowAuth(false)} goHome={() => setShowAuth(false)} />
            <button type="button" onClick={() => setShowAuth(false)} aria-label="Close sign in"
              style={{ position: 'fixed', top: 14, right: 14, zIndex: 10001, width: 40, height: 40, borderRadius: '50%', border: '1px solid rgba(255,255,255,0.25)', background: 'rgba(15,25,35,0.6)', color: '#fff', fontSize: 18, cursor: 'pointer' }}>
              ✕
            </button>
          </div>
        )}
      </>
    );
  }

  if (status === 'success') {
    return card(
      <>
        <h2 style={{ color: B.forest, marginTop: 0 }}>You&apos;re in!</h2>
        <p style={{ color: B.muted }}>
          {trip && trip.title ? `You can now help plan "${trip.title}". It's in My Trips under Shared with me.` : 'Invite accepted.'}
        </p>
        {trip && trip.id && <button type="button" style={btn} onClick={() => navigate(`/trips/${trip.id}`)}>Open trip</button>}
        <button type="button" style={btnAlt} onClick={() => navigate('/mytrips')}>My Trips</button>
      </>,
    );
  }

  if (status === 'error') {
    const wrongAccount = errorInfo && errorInfo.status === 403;
    const invitedEmail = wrongAccount && errorInfo.invitedEmail;
    return card(
      <>
        <h2 style={{ color: '#c0392b', marginTop: 0 }}>Couldn&apos;t accept invite</h2>
        {wrongAccount ? (
          <p style={{ color: B.muted, overflowWrap: 'anywhere' }}>
            This invite is for <strong>{invitedEmail || 'a different email'}</strong>, but you&apos;re signed in as{' '}
            <strong>{user && user.email}</strong>
            {errorInfo.code === 'INVITER' ? ' (you sent this invite)' : ''}. Switch to the invited account to join the trip.
          </p>
        ) : (
          <p style={{ color: B.muted }}>{message}</p>
        )}
        {wrongAccount && <button type="button" style={btn} onClick={switchAccount}>Switch account</button>}
        <button type="button" style={wrongAccount ? btnAlt : btn} onClick={() => navigate('/')}>Go home</button>
      </>,
    );
  }

  return card(<p style={{ color: B.muted }}>Accepting invite…</p>);
}
